import React from 'react';
import Button from "../UI/Button";
import {ITEM_VARIANT_PROPERTY_DUPLICATED_ERROR_CODE} from "../../utility/utility";

export const emptyPropertyRow = () => ({name : '', value : ''});

// Incomplete rows are dropped, so a half-filled row never reaches the API.
// Spaces alone count as empty, because the API trims them away.
const completeProperties = (properties) => properties.filter((property) => property.name.trim() && property.value.trim());

// The API takes properties as a list of {name, value} pairs, so a name can hold
// any character, brackets included.
export const toPropertyList = (properties) => completeProperties(properties)
    .map((property) => ({name : property.name, value : property.value}));

// The API rejects a repeated name, comparing trimmed names ignoring case, so this
// does too. Returns the repeat as typed, or null.
export const findDuplicatePropertyName = (properties) => {
    const seen = new Set();

    for (const property of completeProperties(properties)) {
        const key = property.name.trim().toLowerCase();
        if (seen.has(key)) {
            return property.name;
        }
        seen.add(key);
    }

    return null;
};

// The database also counts names that differ only in accents or ß/ss as equal.
// Matching that here is not worth it, so the API's refusal is explained instead.
export const propertySaveErrorMessage = (error) => error.code === ITEM_VARIANT_PROPERTY_DUPLICATED_ERROR_CODE
    ? 'Two properties of a variant have the same name. Names count as the same regardless of case, accents or ß/ss.'
    : error.message;

// Edits a variant's `properties` as a list of {name, value} rows, e.g.
// Size: M, Color: Pink. Callers convert the rows with toPropertyList on submit.
const PropertyRowsEditor = (props) => {
    const properties = props.properties;

    const handlePropertyChange = (index, field, value) => {
        const next = properties.map((property, i) => i === index ? {...property, [field] : value} : property);
        props.onChange(next);
    };

    const handleAddProperty = () => {
        props.onChange([...properties, emptyPropertyRow()]);
    };

    const handleRemoveProperty = (index) => {
        props.onChange(properties.filter((property, i) => i !== index));
    };

    return (
        <div>
            {properties.map((property, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                    <input
                        type="text"
                        placeholder="Property name (e.g. Size)"
                        value={property.name}
                        onChange={(event) => handlePropertyChange(index, 'name', event.target.value)}
                        className="border rounded-lg py-2 px-3 w-1/2 text-gray-700 focus:outline-none focus:shadow-outline"
                    />
                    <input
                        type="text"
                        placeholder="Value (e.g. M)"
                        value={property.value}
                        onChange={(event) => handlePropertyChange(index, 'value', event.target.value)}
                        className="border rounded-lg py-2 px-3 w-1/2 text-gray-700 focus:outline-none focus:shadow-outline"
                    />
                    <Button title="X" bgColor="red" handleClick={() => handleRemoveProperty(index)}/>
                </div>
            ))}
            <Button title="+ Add property" bgColor="gray" handleClick={handleAddProperty}/>
        </div>
    );
};

export default PropertyRowsEditor;
